import { Box } from "@mui/material"
import { getCoreRowModel } from "@tanstack/react-table"
import type { FC } from "react"
import React, { useMemo, useRef } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { CompanyRegistration, CompanyRegistrationType, SignedUrl } from "@/graphql/codegen/graphql"
import { CompanyRegistrationType as CompanyRegistrationTypes } from "@/graphql/codegen/graphql"
import useRegistrationColumns from "@/screens/CompanySettings/Registration/tables/useRegistrationColumns"
import type { OpenRegistrationDialogFn } from "@/screens/CompanySettings/Registration/types"
import { formatLocationAddress } from "@/utils/utils"

interface RegistrationTableProps {
  data: CompanyRegistration[]
  fileData: SignedUrl[]
  tab: CompanyRegistrationType
  openRegistrationDialog: OpenRegistrationDialogFn
  onRemove: (registration: CompanyRegistration) => void
  fetching?: boolean
}

const RegistrationTable: FC<RegistrationTableProps> = ({
  data,
  fileData,
  openRegistrationDialog,
  onRemove,
  tab,
  fetching,
}) => {
  const columns = useRegistrationColumns({
    openRegistrationDialog,
    onRemove,
    fileData,
    tab,
  })
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const tabData = useMemo(() => {
    const filteredData = data.filter((registration) => registration.registrationType === tab)
    if (tab === CompanyRegistrationTypes.Federal) {
      return filteredData // Skip sorting for Federal tab as it does not have location information
    }
    return filteredData.sort((a, b) => {
      const addressA = formatLocationAddress(a.location).toLowerCase()
      const addressB = formatLocationAddress(b.location).toLowerCase()
      return addressA.localeCompare(addressB)
    })
  }, [data, tab])

  return (
    <Box className='flex h-full flex-col'>
      <AerosBaseTable
        features={{
          table: {
            stickyHeader: true,
          },
          tanstackOptions: {
            data: tabData,
            columns,
            state: {
              isLoading: fetching,
            },
            enableSortingRemoval: false,
            enableSorting: false,
            getCoreRowModel: getCoreRowModel(),
          },
        }}
        slotProps={{
          container: {
            ref: tableContainerRef,
          },
          bodyRow: {
            className: "hover:bg-primary-100 cursor-pointer",
          },
        }}
        noDataMessage='No registrations found'
      />
    </Box>
  )
}

export default RegistrationTable
